import { Stack, Typography } from '@mui/material';
import { CurrencySymbol } from '../atoms/CurrencySymbol';
import { useTranslation } from 'react-i18next';
import { useBetslipBonus } from '../../hooks/useBetslipBonus';
import { WalletType } from '../../types/enums';

type BetslipWinningAmountDisplayProps = {
  maxPayout: number;
  isSystemTicket: boolean;
  selectedWallet: WalletType;
};

export const BetslipWinningAmountDisplay = ({
  maxPayout,
  isSystemTicket,
  selectedWallet,
}: BetslipWinningAmountDisplayProps) => {
  const { t } = useTranslation();

  const {
    bonusAmount,
    bonusPercentage,
    totalWinningAmount,
    showBonus,
    hasActiveCampaigns,
    isBonusWallet,
    showAddMoreSelectionsMsg,
    showBonusNotAvailableMsg,
    eligibility,
  } = useBetslipBonus({ maxPayout, isSystemTicket, selectedWallet });

  if (!hasActiveCampaigns || isBonusWallet) {
    return (
      <Stack
        sx={{
          px: 1.5,
          py: 1,
        }}
      >
        <Typography
          variant='body3'
          sx={{
            color: 'neutral.700',
            textAlign: 'right',
          }}
        >
          {t('maxPossibleWinning')}: <CurrencySymbol />
          {maxPayout.toFixed(2)}
        </Typography>
      </Stack>
    );
  }

  const formattedAmounts = {
    bonus: bonusAmount.toFixed(2),
    total: totalWinningAmount.toFixed(2),
    minOdd: eligibility.minRequiredOdd?.toFixed(2),
  };

  return (
    <>
      <Stack
        sx={{
          px: 1.5,
          py: 1,
        }}
      >
        {showBonus && (
          <Typography
            variant='body3'
            sx={{
              color: 'neutral.600',
              textAlign: 'right',
            }}
          >
            {t('bonus')}: <CurrencySymbol />
            {formattedAmounts.bonus} ({bonusPercentage}%)
          </Typography>
        )}
        <Typography
          variant='body3'
          sx={{
            color: 'neutral.700',
            textAlign: 'right',
          }}
        >
          {t('maxPossibleWinning')}: <CurrencySymbol />
          {formattedAmounts.total}
        </Typography>
      </Stack>

      {showAddMoreSelectionsMsg && (
        <InfoMessage>
          {t('bonusInfo.addMoreSelections', {
            count: eligibility.selectionsNeeded,
            odd: formattedAmounts.minOdd,
            percentage: eligibility.nextBonusPercentage,
          })}
        </InfoMessage>
      )}

      {showBonusNotAvailableMsg && <InfoMessage>{t('bonusInfo.bonusNotAvailable')}</InfoMessage>}
    </>
  );
};

const InfoMessage = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant='body3'
    sx={{
      textAlign: 'center',
      backgroundColor: 'info.main',
      color: 'neutral.600',
      py: 0.5,
      px: 1,
    }}
  >
    {children}
  </Typography>
);
